import api from '@/js/services/axios'

/**
 * API for membership assignments (participant–org agreements) in the memberships domain.
 * Backend: GET/POST /api/private/v1/org/{orgId}/memberships
 * Per-participant operations (update, stop, delete, paid, invoice) use TParticipantRepository.
 */
const BASE = 'private/v1/org/{orgId}/memberships/{membershipId}'

function getPath(membershipId) {
  return BASE.replace('{membershipId}', membershipId)
}

export default {
  /**
   * Update a membership assignment (org-level endpoint if available)
   * @param {number} membershipId - The membership assignment ID
   * @param {Object} membershipData - Updated data
   * @returns {Promise<Object>} Updated membership
   */
  updateMembership: async (membershipId, membershipData) => {
    return new Promise((resolve, reject) => {
      api.patch(getPath(membershipId), { is_patch: true, ...membershipData })
        .then(response => resolve(response.data))
        .catch(error => reject(error))
    })
  },
  /**
  * Update the paid status of a membership assignment.
  * @param {number} membershipId - The membership assignment ID.
  * @param {boolean} paid - The paid status.
  * @returns {Promise<Object>} Updated membership
  */
  setPaid: async (membershipId, paid) => {
    return new Promise((resolve, reject) => {
      api.patch(getPath(membershipId), { is_patch: true, paid })
        .then(response => resolve(response.data))
        .catch(error => reject(error))
    })
  },

  /**
   * Delete a membership assignment (org-level)
   * @param {number} membershipId - The membership assignment ID
   * @returns {Promise<void>}
   */
  deleteMembership: async (membershipId) => {
    return new Promise((resolve, reject) => {
      api.delete(getPath(membershipId))
        .then(() => resolve())
        .catch(error => reject(error))
    })
  },

  /**
   * Stop a membership assignment (org-level, no participant in path)
   * @param {number} membershipId - The membership assignment ID
   * @param {string} stopDate - Stop date (YYYY-MM-DD)
   * @returns {Promise<Object>} Updated membership
   */
  stopMembership: async (membershipId, stopDate) => {
    return new Promise((resolve, reject) => {
      api.patch(getPath(membershipId) + '/stop', { stop_date: stopDate })
        .then(response => resolve(response.data))
        .catch(error => reject(error))
    })
  },

  /**
   * Download invoice PDF for a membership assignment
   * @param {number} membershipId - The membership assignment ID
   * @returns {Promise<void>}
   */
  downloadMembershipInvoice: async (membershipId) => {
    return new Promise((resolve, reject) => {
      api.get(getPath(membershipId) + '/invoice', {
        responseType: 'blob'
      })
        .then(response => {
          // Create a blob URL and trigger download
          const blob = new Blob([response.data], { type: 'application/pdf' })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `invoice-${membershipId}.pdf`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
          resolve()
        })
        .catch((error) => {
          reject(error)
        })
    })
  }
}
